/**
 * Definizione dei ruoli utente e relative autorizzazioni.
 */

export const ROLES = {
  FORNITORE: 'fornitore',
  HQ: 'hq',
  FASTWEB: 'fastweb',
  DITTA: 'ditta',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

export const ROLE_LABELS: Record<Role, string> = {
  [ROLES.FORNITORE]: 'Professionista',
  [ROLES.HQ]: 'HQ / Sede Centrale',
  [ROLES.FASTWEB]: 'Fastweb',
  [ROLES.DITTA]: 'Ditta',
};

export const ROLE_COLORS: Record<Role, { bg: string; text: string }> = {
  [ROLES.FORNITORE]: { bg: 'bg-green-50', text: 'text-green-700' },
  [ROLES.HQ]: { bg: 'bg-yellow-50', text: 'text-fw-dark' },
  [ROLES.FASTWEB]: { bg: 'bg-fw-purple-subtle', text: 'text-fw-purple' },
  [ROLES.DITTA]: { bg: 'bg-blue-50', text: 'text-fw-blue' },
};

export interface Permissions {
  canCreatePractice: boolean;
  canEditPractice: boolean;
  canDeletePractice: boolean;
  canSetDocCaricati: boolean;
  canClosePractice: boolean;
  canRejectPractice: boolean;
  canApproveByDitta: boolean;
  canRejectByDitta: boolean;
  canUpdateQuotation: boolean;
  canUploadExcel: boolean;
  canExportCSV: boolean;
  canViewDashboard: boolean;
  canViewPlots: boolean;
  canViewReport: boolean;
  canViewAuditLog: boolean;
  canInviteUsers: boolean;
}

/**
 * Ruoli che ciascun ruolo può invitare:
 * - HQ: può invitare tutti (hq, fastweb, fornitore, ditta)
 * - Fastweb: può invitare fastweb, fornitore, ditta
 * - Fornitore / Ditta: non possono invitare nessuno
 */
export const INVITABLE_ROLES: Record<Role, Role[]> = {
  [ROLES.HQ]: [ROLES.HQ, ROLES.FASTWEB, ROLES.FORNITORE, ROLES.DITTA],
  [ROLES.FASTWEB]: [ROLES.FASTWEB, ROLES.FORNITORE, ROLES.DITTA],
  [ROLES.FORNITORE]: [],
  [ROLES.DITTA]: [],
};

/** Ritorna i ruoli che l'utente corrente può invitare. */
export function getInvitableRoles(userRole: string | null): Role[] {
  return INVITABLE_ROLES[userRole as Role] || [];
}

const PERMISSIONS: Record<Role, Permissions> = {
  [ROLES.FORNITORE]: {
    canCreatePractice: true,
    canEditPractice: true,
    canDeletePractice: true,
    canSetDocCaricati: true,
    canClosePractice: false,
    canRejectPractice: false,
    canApproveByDitta: false,
    canRejectByDitta: false,
    canUpdateQuotation: true,
    canUploadExcel: false,
    canExportCSV: true,
    canViewDashboard: true,
    canViewPlots: false,
    canViewReport: false,
    canViewAuditLog: false,
    canInviteUsers: false,
  },
  [ROLES.HQ]: {
    canCreatePractice: false,
    canEditPractice: false,
    canDeletePractice: false,
    canSetDocCaricati: false,
    canClosePractice: true,
    canRejectPractice: true,
    canApproveByDitta: false,
    canRejectByDitta: false,
    canUpdateQuotation: false,
    canUploadExcel: false,
    canExportCSV: true,
    canViewDashboard: true,
    canViewPlots: true,
    canViewReport: true,
    canViewAuditLog: true,
    canInviteUsers: true,
  },
  [ROLES.FASTWEB]: {
    canCreatePractice: false,
    canEditPractice: false,
    canDeletePractice: false,
    canSetDocCaricati: false,
    canClosePractice: false,
    canRejectPractice: false,
    canApproveByDitta: false,
    canRejectByDitta: false,
    canUpdateQuotation: false,
    canUploadExcel: false,
    canExportCSV: true,
    canViewDashboard: true,
    canViewPlots: true,
    canViewReport: true,
    canViewAuditLog: true,
    canInviteUsers: true,
  },
  [ROLES.DITTA]: {
    canCreatePractice: false,
    canEditPractice: false,
    canDeletePractice: false,
    canSetDocCaricati: false,
    canClosePractice: false,
    canRejectPractice: false,
    canApproveByDitta: true,
    canRejectByDitta: true,
    canUpdateQuotation: false,
    canUploadExcel: false,
    canExportCSV: true,
    canViewDashboard: true,
    canViewPlots: false,
    canViewReport: false,
    canViewAuditLog: false,
    canInviteUsers: false,
  },
};

const NO_PERMISSIONS: Permissions = {
  canCreatePractice: false,
  canEditPractice: false,
  canDeletePractice: false,
  canSetDocCaricati: false,
  canClosePractice: false,
  canRejectPractice: false,
  canApproveByDitta: false,
  canRejectByDitta: false,
  canUpdateQuotation: false,
  canUploadExcel: false,
  canExportCSV: false,
  canViewDashboard: false,
  canViewPlots: false,
  canViewReport: false,
  canViewAuditLog: false,
  canInviteUsers: false,
};

/** Profilo minimo per le restrizioni per-profilo (Fastweb: gate ETO). */
export interface PermissionProfile {
  isEto?: boolean | null;
}

/**
 * Ritorna i permessi per un ruolo. Default: nessun permesso.
 *
 * Secondo parametro opzionale `profile`: se fornito (anche null = profilo in
 * caricamento, default sicuro) e il ruolo è Fastweb NON-ETO, i permessi di
 * amministrazione accessi vengono rimossi — gli utenti Fastweb territoriali
 * sono in sola consultazione e non gestiscono inviti/utenti.
 * Chiamate senza profilo (call-site legacy, es. canAccessRoute) restano
 * invariate: permessi puri di ruolo.
 */
export function getPermissions(role: string | null, profile?: PermissionProfile | null): Permissions {
  const base = PERMISSIONS[role as Role] || NO_PERMISSIONS;
  if (role === ROLES.FASTWEB && profile !== undefined && profile?.isEto !== true) {
    return { ...base, canInviteUsers: false };
  }
  return base;
}

/** Verifica se un ruolo ha accesso a una determinata route. */
export function canAccessRoute(role: string, path: string): boolean {
  const perms = getPermissions(role);
  if (path === '/dashboard') return perms.canViewDashboard;
  if (path === '/data') return true;
  if (path === '/plots') return perms.canViewPlots;
  if (path === '/report') return perms.canViewReport;
  return true;
}
